import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { Project, Settings, ProjectImage, ProjectTeamMember, Consultant, TeamMember, Associate } from '../../types';
import { GoogleGenAI, Type } from '@google/genai';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import RichTextEditor from '../../components/RichTextEditor';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '../../hooks/useToast';
import { useCollection } from '../../hooks/useCollection';

const fileToGenerativePart = async (file: File) => {
    const base64EncodedDataPromise = new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(file);
    });
    return {
      inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
    };
};

interface AdminProjectFormPageProps {
    settings: Settings;
    isModalMode?: boolean;
    initialProject?: Project | null;
    onModalSave?: () => void;
    onModalClose?: () => void;
}

type EditableImage = {
    id: string;
    url: string;
};

const generateSlug = (title: string) => {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // remove invalid chars
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-'); // collapse dashes
};

const SortableImage: React.FC<{ image: EditableImage, onRemove: () => void, onFileChange: (file: File) => void, uploadStatus?: 'uploading' | 'done' }> = ({ image, onRemove, onFileChange, uploadStatus }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: image.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="relative group aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center touch-none">
            {uploadStatus === 'uploading' && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                    <div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div>
                </div>
            )}
            {image.url ? (
                <>
                    <img src={image.url} alt={`Project image`} className="w-full h-full object-cover rounded-md" />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="text-white text-xs bg-black bg-opacity-70 px-2 py-1 rounded">Change</button>
                        <button type="button" onClick={onRemove} className="ml-2 text-white text-xs bg-red-600 bg-opacity-90 px-2 py-1 rounded">Remove</button>
                    </div>
                </>
            ) : (
                <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                        Upload Image
                    </button>
                </div>
            )}
             <input 
                type="file" 
                ref={fileInputRef}
                onChange={(e) => e.target.files?.[0] && onFileChange(e.target.files[0])}
                accept="image/png, image/jpeg, image/gif, image/webp" 
                className="hidden"
            />
        </div>
    )
}

const AdminProjectFormPage: React.FC<AdminProjectFormPageProps> = ({ settings, isModalMode = false, initialProject = null, onModalSave, onModalClose }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id || (isModalMode && initialProject));
    const { showToast } = useToast();
    const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
    const [isGeneratingDetails, setIsGeneratingDetails] = useState(false);
    const [imageUploadStatus, setImageUploadStatus] = useState<{[key: string]: 'uploading' | 'done'}>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [tempProjectId] = useState(() => `TEMP_${Date.now()}`);
    const imageUploadInputRef = useRef<HTMLInputElement>(null);
    const [aiImageFile, setAiImageFile] = useState<File | null>(null);
    
    const [project, setProject] = useState<Omit<Project, 'id' | 'images' | 'projectTeam'>>({
        title: '',
        slug: '',
        category: settings.projectCategories?.[0] || '',
        year: new Date().getFullYear(),
        description: '',
        details: '',
        client: '',
        location: '',
        status: 'Proposal',
        published: true,
        featured: false,
        associateSlug: '',
        price: undefined,
        order: 0,
    });
    
    const [editableImages, setEditableImages] = useState<EditableImage[]>([]);
    const [projectTeam, setProjectTeam] = useState<ProjectTeamMember[]>([]);
    const [descKeywords, setDescKeywords] = useState('');
    const [detailsKeywords, setDetailsKeywords] = useState('');
    const [detailLevel, setDetailLevel] = useState<'Simple' | 'Moderate' | 'Detailed'>('Moderate');

    const { data: consultants } = useCollection<Consultant>('consultants');
    const { data: teamMembers } = useCollection<TeamMember>('teamMembers');
    const { data: associates } = useCollection<Associate>('associates');

    useEffect(() => {
        const populateForm = (data: Project | null) => {
            if (data) {
                const { images, projectTeam: team, ...projectData } = data;
                setProject({ associateSlug: '', ...projectData, order: data.order ?? 0 });
                setEditableImages((images?.length > 0 ? images : []).map((img, i) => ({ id: `img-${data.id}-${i}`, url: img.url })));
                setProjectTeam(team || []);
            }
        };

        const setupNewProject = async () => {
            const projectsCollection = collection(db, 'projects');
            const snapshot = await getDocs(projectsCollection);
            setProject(prev => ({ ...prev, order: snapshot.size }));
        };

        const fetchProjectForPage = async (projectId: string) => {
            const docRef = doc(db, 'projects', projectId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                populateForm(docSnap.data() as Project);
            } else {
                showToast('Project not found.', { type: 'error' });
                navigate('/admin/dashboard');
            }
        };

        const loadData = async () => {
            setIsLoading(true);
            if (isModalMode) {
                if (initialProject) { // Editing in modal
                    populateForm(initialProject);
                } else { // New project in modal
                    await setupNewProject();
                }
            } else if (id) { // Editing on admin page
                await fetchProjectForPage(id);
            } else { // New project on admin page
                await setupNewProject();
            }
            setIsLoading(false);
        };

        loadData();
    }, [id, isModalMode, initialProject, navigate, showToast]);


    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setEditableImages((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        if (e.target.type === 'checkbox') {
            const { checked } = e.target as HTMLInputElement;
            setProject(prev => ({...prev, [name]: checked }));
        } else {
            if (name === 'year' || name === 'price') {
                 const numValue = parseInt(value);
                 if (name === 'year') {
                     setProject(prev => ({ ...prev, year: numValue || new Date().getFullYear()}));
                 } else {
                     setProject(prev => ({ ...prev, price: isNaN(numValue) ? undefined : numValue}));
                 }
            } else {
                setProject(prev => ({ ...prev, [name]: value }));
            }

            if (name === 'title' && !isEditing) {
                setProject(prev => ({...prev, slug: generateSlug(value)}));
            }
        }
    };
    
    const handleTeamChange = (index: number, selectedValue: string) => {
        const [type, memberId] = selectedValue.split(':');
        let selectedMember: ProjectTeamMember | null = null;

        if (type === 'team' && teamMembers) {
            const member = teamMembers.find(m => m.id === memberId);
            if (member) {
                selectedMember = { sourceId: member.id, sourceType: 'team', name: member.name, role: member.role, company: settings.companyName, avatarUrl: member.imageUrl };
            }
        } else if (type === 'consultant' && consultants) {
            const consultant = consultants.find(c => c.id === memberId);
            if (consultant) {
                selectedMember = { sourceId: consultant.id, sourceType: 'consultant', name: consultant.name, role: consultant.role, company: consultant.company, avatarUrl: consultant.avatarUrl };
            }
        }

        if (selectedMember) {
            const newTeam = [...projectTeam];
            newTeam[index] = selectedMember;
            setProjectTeam(newTeam);
        }
    };

    const addTeamMember = () => {
        setProjectTeam([...projectTeam, { sourceId: '', sourceType: 'team', name: '', role: '', company: '' }]);
    };

    const removeTeamMember = (index: number) => {
        setProjectTeam(projectTeam.filter((_, i) => i !== index));
    };

    const handleImageChange = async (imageId: string, file: File) => {
        const maxFileSize = (settings.maxProjectImageSizeMB || 2) * 1024 * 1024;
        if (file.size > maxFileSize) {
            showToast(`Image is too large. Maximum size is ${settings.maxProjectImageSizeMB || 2}MB.`, { type: 'error' });
            // Remove the placeholder if validation fails
            setEditableImages(prev => prev.filter(img => img.id !== imageId));
            return;
        }

        setImageUploadStatus(prev => ({ ...prev, [imageId]: 'uploading' }));
        const currentProjectId = isEditing ? (initialProject?.id || id) : tempProjectId;

        try {
            const storageRef = ref(storage, `projects/${currentProjectId}/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            setEditableImages(prev => prev.map(img => 
                img.id === imageId ? { ...img, url: downloadURL } : img
            ));
            showToast('Image uploaded successfully!', { type: 'success' });
        } catch (error: any) {
            console.error("Error uploading image:", error);
            let message = 'Image upload failed. Please try again.';
            if (error.code === 'storage/unauthorized') {
                message = 'Permission Denied: Please check your Firebase Storage rules to allow write access.';
            }
            showToast(message, { type: 'error' });
            // Remove placeholder on error
            setEditableImages(prev => prev.filter(img => img.id !== imageId));
        } finally {
            setImageUploadStatus(prev => ({ ...prev, [imageId]: 'done' }));
        }
    };
    
    const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        for (const file of Array.from(files)) {
            const tempId = `new-${Date.now()}-${Math.random()}`;
            setEditableImages(prev => [...prev, { id: tempId, url: '' }]);
            handleImageChange(tempId, file);
        }

        if (e.target) e.target.value = '';
    };

    const removeImageField = (idToRemove: string) => {
        setEditableImages(prev => prev.filter(img => img.id !== idToRemove));
    };
    
    const handleGenerateDescription = async () => {
        if (!descKeywords.trim() && !aiImageFile) {
            showToast("Please enter keywords or upload an image to generate a description.", { type: 'error' });
            return;
        }
        setIsGeneratingDesc(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            
            const promptParts: (string | { inlineData: { data: string; mimeType: string; } })[] = [
                `As an expert copywriter for a high-end architectural firm, write a compelling, short project description (under 25 words). The tone should be sophisticated and evocative. Do not use any markdown formatting like asterisks.`
            ];
            
            if (aiImageFile) {
                const imagePart = await fileToGenerativePart(aiImageFile);
                promptParts.push(imagePart);
                promptParts.push(`Describe the building in this image. `);
            }

            if (descKeywords.trim()) {
                promptParts.push(`Incorporate these keywords: "${descKeywords}".`);
            }
            
            const response = await ai.models.generateContent({
                // FIX: The user-reported error was likely caused by using a deprecated model. Updated to a recommended multimodal model.
                model: 'gemini-3-flash-preview',
                contents: { parts: promptParts.map(p => typeof p === 'string' ? { text: p } : p) }
            });

            const text = response.text.trim().replace(/\*\*/g, '');
            if (text) {
                setProject(prev => ({...prev, description: text}));
                showToast('Description generated successfully!', { type: 'success' });
            } else {
                 throw new Error("AI did not return a description.");
            }
        } catch (error) {
            console.error("AI description generation failed:", error);
            showToast("Sorry, there was an error generating the description.", { type: 'error' });
        } finally {
            setIsGeneratingDesc(false);
        }
    };

    const handleGenerateDetails = async () => {
        if (!detailsKeywords.trim()) {
            showToast("Please enter keywords or a prompt for the project details.", { type: 'error' });
            return;
        }
        setIsGeneratingDetails(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            
            const detailInstructions = {
                Simple: 'a concise paragraph',
                Moderate: 'two or three paragraphs with a couple of sections',
                Detailed: 'multiple detailed sections covering concept, design, materials, and challenges'
            };
    
            const context = `
                Project Title: ${project.title || 'Not specified'}
                Category: ${project.category || 'Not specified'}
                Client: ${project.client || 'Not specified'}
                Location: ${project.location || 'Not specified'}
            `;
    
            const prompt = `As an expert copywriter for a high-end architectural firm, write ${detailInstructions[detailLevel]} for a project description. 
            Use the following context: ${context}.
            Base the description on these keywords/instructions: "${detailsKeywords}". 
            The response MUST be in HTML format. Use <h3> tags for main sections (e.g., <h3>Concept</h3>, <h3>Design</h3>) and <p> tags for paragraphs. 
            The tone should be professional, sophisticated, and descriptive. Do not use any markdown formatting like asterisks. If emphasis is needed, use <strong> tags.`;

            const response = await ai.models.generateContent({
                // FIX: Update model to a recommended text model for consistency and better performance.
                model: 'gemini-3-flash-preview',
                contents: prompt,
            });
            const text = response.text.trim().replace(/\*\*/g, '');
            if (text) {
                setProject(prev => ({...prev, details: text}));
                showToast('Project details generated successfully!', { type: 'success' });
            } else {
                 throw new Error("AI did not return any content.");
            }
        } catch (error) {
            console.error("AI details generation failed:", error);
            showToast("Sorry, there was an error generating the project details.", { type: 'error' });
        } finally {
            setIsGeneratingDetails(false);
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);

        const updatedTeam: ProjectTeamMember[] = [];
        if (projectTeam.length > 0) {
            for (const member of projectTeam) {
                if (!member.sourceId) continue; // Skip empty/unselected entries

                if (member.sourceType === 'team' && teamMembers) {
                    const latestData = teamMembers.find(t => t.id === member.sourceId);
                    if (latestData) {
                        updatedTeam.push({
                            sourceId: latestData.id,
                            sourceType: 'team',
                            name: latestData.name,
                            role: latestData.role,
                            company: settings.companyName,
                            avatarUrl: latestData.imageUrl,
                        });
                    }
                } else if (member.sourceType === 'consultant' && consultants) {
                    const latestData = consultants.find(c => c.id === member.sourceId);
                    if (latestData) {
                        updatedTeam.push({
                            sourceId: latestData.id,
                            sourceType: 'consultant',
                            name: latestData.name,
                            role: latestData.role,
                            company: latestData.company,
                            avatarUrl: latestData.avatarUrl,
                        });
                    }
                }
            }
        }

        const projectData = {
            ...project,
            slug: isEditing ? project.slug : generateSlug(project.title),
            images: editableImages
                .map(({ url }) => ({ url }))
                .filter(img => img.url.trim() !== ''),
            projectTeam: updatedTeam
        };
        
        // Ensure associateSlug is set correctly
        const projectToSave = {
            ...projectData,
            associateSlug: projectData.associateSlug || '', // Ensure it's an empty string if null/undefined
        };

        try {
            if (isEditing) {
                const projectId = initialProject?.id || id;
                if (!projectId) throw new Error("Project ID is missing for update.");
                const projectRef = doc(db, 'projects', projectId);
                await updateDoc(projectRef, projectToSave);
                showToast('Project updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'projects'), projectToSave);
                showToast('Project created successfully!', { type: 'success' });
            }
            if (isModalMode && onModalSave) {
                onModalSave();
            } else {
                navigate('/admin/dashboard');
            }
        } catch (error) {
            console.error("Error saving project:", error);
            showToast('Failed to save project. Please check the console.', { type: 'error' });
            setIsSaving(false);
        }
    };
    
    const handleCancel = () => {
        if (isModalMode && onModalClose) {
            onModalClose();
        } else {
            navigate('/admin/dashboard');
        }
    };

    const SparklesIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8-5.8 1.9 5.8 1.9 1.9 5.8 1.9-5.8 5.8-1.9-5.8-1.9z"/></svg>;
    const UploadIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;

    if (isLoading) {
        return <div className="text-center p-8">Loading project details...</div>;
    }

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Project' : 'Add New Project'}</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Main Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700">Project Title</label>
                            <input type="text" name="title" id="title" value={project.title} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                            {isEditing ? (
                                <p className="mt-1 text-xs text-gray-500 italic">Slug cannot be changed after creation: /project/{project.slug}</p>
                            ) : (
                                project.slug && <p className="mt-1 text-xs text-gray-500 italic">Slug preview: /project/{project.slug}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
                            <select name="category" id="category" value={project.category} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                {(settings.projectCategories || []).map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                         <div>
                            <label htmlFor="associateSlug" className="block text-sm font-medium text-gray-700">Associate Company</label>
                            <select name="associateSlug" id="associateSlug" value={project.associateSlug || ''} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                <option value="">None (Main Firm)</option>
                                {associates?.map(assoc => (
                                    <option key={assoc.id} value={assoc.slug}>{assoc.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="client" className="block text-sm font-medium text-gray-700">Client</label>
                            <input type="text" name="client" id="client" value={project.client} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
                            <input type="text" name="location" id="location" value={project.location} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="year" className="block text-sm font-medium text-gray-700">Year</label>
                            <input type="number" name="year" id="year" value={project.year} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="price" className="block text-sm font-medium text-gray-700">Project Price / Value (Optional)</label>
                            <input type="number" name="price" id="price" value={project.price || ''} onChange={handleChange} placeholder="e.g., 1500000" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                            <p className="mt-1 text-xs text-gray-500">Enter a numerical value. No commas or currency symbols.</p>
                        </div>
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                            <select name="status" id="status" value={project.status} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                <option>Proposal</option>
                                <option>Concept</option>
                                <option>Under Construction</option>
                                <option>Completed</option>
                            </select>
                        </div>
                    </div>
                    
                     {/* AI Description Section */}
                     {settings.enableAiFeatures && (
                        <div className="space-y-2">
                            <label htmlFor="descKeywords" className="block text-sm font-medium text-gray-700">AI Prompt for Short Description</label>
                            <div className="flex flex-col md:flex-row gap-2">
                                <textarea 
                                    name="descKeywords" 
                                    id="descKeywords" 
                                    rows={2} 
                                    value={descKeywords} 
                                    onChange={(e) => setDescKeywords(e.target.value)} 
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                                    placeholder="e.g., modern villa, cliffside, ocean view, minimalist interior"
                                ></textarea>
                                <div className="flex gap-2">
                                    <label className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 flex items-center whitespace-nowrap cursor-pointer">
                                        <UploadIcon className="h-5 w-5 mr-2"/>
                                        {aiImageFile ? "Change Image" : "Upload Image for AI"}
                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => setAiImageFile(e.target.files ? e.target.files[0] : null)} />
                                    </label>
                                    <button type="button" onClick={handleGenerateDescription} disabled={isGeneratingDesc} className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 flex items-center whitespace-nowrap">
                                        <SparklesIcon className={`h-5 w-5 mr-2 ${isGeneratingDesc ? 'animate-pulse' : ''}`}/>
                                        {isGeneratingDesc ? 'Generating...' : 'Generate with AI'}
                                    </button>
                                </div>
                            </div>
                             {aiImageFile && <p className="text-xs text-gray-500 mt-1">Image selected: {aiImageFile.name}</p>}
                        </div>
                     )}

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Short Description (for card)</label>
                        <textarea name="description" id="description" rows={3} value={project.description} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"></textarea>
                    </div>

                    {/* Image Upload Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Images</label>
                        <p className="text-xs text-gray-500 mb-2">Drag and drop to reorder. The first image is the main thumbnail.</p>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={editableImages.map(img => img.id)} strategy={rectSortingStrategy}>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                                    {editableImages.map((image) => (
                                        <SortableImage 
                                            key={image.id}
                                            image={image} 
                                            onRemove={() => removeImageField(image.id)}
                                            onFileChange={(file) => handleImageChange(image.id, file)}
                                            uploadStatus={imageUploadStatus[image.id]}
                                        />
                                    ))}
                                     <input
                                        type="file"
                                        multiple
                                        ref={imageUploadInputRef}
                                        onChange={handleAddImages}
                                        className="hidden"
                                        accept="image/png, image/jpeg, image/gif, image/webp"
                                    />
                                    <button type="button" onClick={() => imageUploadInputRef.current?.click()} className="aspect-square border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-500 transition-colors">
                                        <svg className="w-8 h-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="1"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                        <span className="text-xs mt-1">Add Images</span>
                                    </button>
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                    
                     {/* Project Team Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Project Team</label>
                        <div className="space-y-3 rounded-md border border-gray-200 p-4">
                            {projectTeam.map((member, index) => (
                                <div key={index} className="flex gap-2 items-center">
                                    <select
                                        value={member.sourceId ? `${member.sourceType}:${member.sourceId}` : ''}
                                        onChange={(e) => handleTeamChange(index, e.target.value)}
                                        className="flex-grow w-full px-3 py-2 border border-gray-300 bg-white rounded-md"
                                    >
                                        <option value="" disabled>{member.name || 'Select a Team Member'}</option>
                                        <optgroup label="Internal Team">
                                            {teamMembers?.map(m => (
                                                <option key={`team-${m.id}`} value={`team:${m.id}`}>{m.name} ({m.role})</option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Consultants">
                                            {consultants?.map(c => (
                                                <option key={`consultant-${c.id}`} value={`consultant:${c.id}`}>{c.name} ({c.company})</option>
                                            ))}
                                        </optgroup>
                                    </select>
                                    <button 
                                        type="button" 
                                        onClick={() => removeTeamMember(index)}
                                        className="flex-shrink-0 w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors text-sm font-bold"
                                        aria-label={`Remove ${member.name}`}
                                    >
                                        X
                                    </button>
                                </div>
                            ))}
                            <div className="flex items-center gap-4 mt-4">
                                <button type="button" onClick={addTeamMember} className="text-sm font-semibold text-gray-800 hover:text-black inline-flex items-center gap-2">
                                    <span className="font-bold text-lg leading-none">+</span> Add Team Member
                                </button>
                                <div className="flex-grow text-right space-x-4">
                                    <Link to="/admin/team" target="_blank" className="text-sm text-indigo-600 hover:underline">
                                        Manage Internal Team
                                    </Link>
                                    <Link to="/admin/consultants" target="_blank" className="text-sm text-indigo-600 hover:underline">
                                        Manage Consultants
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* AI Full Details Section */}
                    {settings.enableAiFeatures && (
                        <div className="space-y-2">
                            <label htmlFor="detailsKeywords" className="block text-sm font-medium text-gray-700">AI Prompt for Project Details</label>
                             <div className="p-4 border rounded-md bg-gray-50 space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Detail Level</label>
                                    <div className="flex items-center gap-4">
                                        {(['Simple', 'Moderate', 'Detailed'] as const).map(level => (
                                            <label key={level} className="flex items-center text-sm">
                                                <input
                                                    type="radio"
                                                    name="detailLevel"
                                                    value={level}
                                                    checked={detailLevel === level}
                                                    onChange={() => setDetailLevel(level)}
                                                    className="h-4 w-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                                                />
                                                <span className="ml-2 text-gray-700">{level}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <textarea 
                                        name="detailsKeywords" 
                                        id="detailsKeywords" 
                                        rows={2} 
                                        value={detailsKeywords} 
                                        onChange={(e) => setDetailsKeywords(e.target.value)} 
                                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                                        placeholder="e.g., Two-story residence, focus on natural light, oak flooring, floor-to-ceiling windows..."
                                    ></textarea>
                                    <button type="button" onClick={handleGenerateDetails} disabled={isGeneratingDetails} className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 flex items-center whitespace-nowrap">
                                        <SparklesIcon className={`h-5 w-5 mr-2 ${isGeneratingDetails ? 'animate-pulse' : ''}`}/>
                                        {isGeneratingDetails ? 'Generating...' : 'Generate Details'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* Full Details */}
                    <div>
                        <label htmlFor="details" className="block text-sm font-medium text-gray-700">Full Project Details</label>
                        <RichTextEditor
                            value={project.details}
                            onChange={(value) => setProject(prev => ({ ...prev, details: value }))}
                        />
                    </div>
                    
                    {/* Publication Status */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start">
                           <div className="flex items-center h-5">
                             <input id="published" name="published" type="checkbox" checked={project.published} onChange={handleChange} className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" />
                           </div>
                           <div className="ml-3 text-sm">
                             <label htmlFor="published" className="font-medium text-gray-700">Published</label>
                             <p className="text-gray-500">Uncheck this to save project as a draft.</p>
                           </div>
                        </div>
                         <div className="flex items-start">
                           <div className="flex items-center h-5">
                             <input id="featured" name="featured" type="checkbox" checked={project.featured} onChange={handleChange} className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" />
                           </div>
                           <div className="ml-3 text-sm">
                             <label htmlFor="featured" className="font-medium text-gray-700">Featured</label>
                             <p className="text-gray-500">Show this project in the homepage hero slider.</p>
                           </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={handleCancel} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                            {isSaving ? 'Saving...' : 'Save Project'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminProjectFormPage;